import React from 'react';

interface WeeklyChartProps {
  data: number[];
  labels: string[];
  title: string;
  color: string;
  maxValue?: number;
}

export function WeeklyChart({ data, labels, title, color, maxValue }: WeeklyChartProps) {
  const max = maxValue || Math.max(...data, 1);

  return (
    <div className="bg-white rounded-2xl p-6">
      <h3 className="text-lg font-bold text-gray-800 mb-4">{title}</h3>
      <div className="flex items-end justify-between gap-2 h-40">
        {data.map((value, index) => {
          const height = (value / max) * 100;
          const isToday = index === new Date().getDay();

          return (
            <div key={index} className="flex-1 flex flex-col items-center gap-2">
              <div className="text-xs font-semibold text-gray-600">{value}</div>
              <div className="w-full bg-gray-100 rounded-t-lg relative" style={{ height: '120px' }}>
                <div
                  className={`absolute bottom-0 w-full rounded-t-lg transition-all duration-500 ${
                    isToday ? color : 'bg-gray-300'
                  }`}
                  style={{ height: `${height}%` }}
                />
              </div>
              <div className={`text-xs font-medium ${isToday ? 'text-purple-600' : 'text-gray-500'}`}>
                {labels[index]}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface StreakDisplayProps {
  currentStreak: number;
  longestStreak: number;
  dailyGoal: number;
  dailyProgress: number;
}

export function StreakDisplay({
  currentStreak,
  longestStreak,
  dailyGoal,
  dailyProgress,
}: StreakDisplayProps) {
  const progress = Math.min((dailyProgress / dailyGoal) * 100, 100);
  const isGoalReached = dailyProgress >= dailyGoal;

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Current Streak */}
      <div className="bg-gradient-to-br from-orange-400 to-red-500 rounded-3xl p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div className="text-5xl">🔥</div>
          <div className="text-right">
            <div className="text-4xl font-bold">{currentStreak}</div>
            <div className="text-orange-100 text-sm">day streak</div>
          </div>
        </div>
        <div className="text-sm opacity-90">
          Longest streak: {longestStreak} day{longestStreak !== 1 ? 's' : ''}
        </div>
        <div className="mt-2 text-xs opacity-75">
          Keep learning daily to maintain your streak!
        </div>
      </div>

      {/* Daily Goal */}
      <div className="bg-gradient-to-br from-green-400 to-emerald-500 rounded-3xl p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div className="text-5xl">{isGoalReached ? '🎯' : '📈'}</div>
          <div className="text-right">
            <div className="text-4xl font-bold">{dailyProgress}/{dailyGoal}</div>
            <div className="text-green-100 text-sm">words today</div>
          </div>
        </div>
        <div className="relative h-3 bg-white/30 rounded-full overflow-hidden">
          <div
            className="absolute h-full bg-white rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="mt-2 text-sm opacity-90">
          {isGoalReached
            ? '🎉 Daily goal reached! Amazing work!'
            : `${dailyGoal - dailyProgress} more to reach your goal`}
        </div>
      </div>
    </div>
  );
}
